import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getLikesSettings, manageCommentsSettings } from 'services/actions/likesService';
import Modal from 'components/actions/ActionSettingsModal';
import { toast } from 'react-toastify';
import Spinner from 'components/common/Spinner';
import { useTheme } from 'contexts/ThemeContext';

interface LikesSettingsProps {
  selectedUser: string;
}

interface Setting {
  _id: string;
  action_id: string;
  settings_type: string;
  description: string;
  settings_value: any;
  is_active?: boolean;
  category_enabled?: boolean;
}

interface SettingsCategory {
  title: string;
  options: Setting[];
}

const LikesSettings: React.FC<LikesSettingsProps> = ({ selectedUser }) => {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [settings, setSettings] = useState<Setting[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingKeyword, setEditingKeyword] = useState<{ index: number; optionIndex: number }>({ index: -1, optionIndex: -1 });
  const [editingTemplate, setEditingTemplate] = useState<{ index: number; optionIndex: number }>({ index: -1, optionIndex: -1 });
  const [settingsType, setSettingsType] = useState<string>('');
  const [settingsSubType, setSettingsSubType] = useState<string>('');
  const [oldKeywordName, setOldKeywordName] = useState<string>('');
  const [oldHeader, setOldHeader] = useState<string>('');
  const [oldValue, setOldValue] = useState<string>('');
  const [actionID, setActionID] = useState<string>('');

  const [isModalOpen, setModalOpen] = useState<boolean>(false);

  // Design tokens
  const mainBg = isDark ? '#0F172B' : '#FFFFFF';
  const sectionHeaderColor = isDark ? '#E2E8F0' : '#314158';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const labelColor = isDark ? '#E2E8F0' : '#314158';
  const descriptionColor = isDark ? '#90A1B9' : '#62748E';
  const switchOnBg = isDark ? '#51A2FF' : '#2B7FFF';
  const switchKnob = isDark ? '#0F172B' : '#FFFFFF';
  const switchOffBg = isDark ? '#314158' : '#E2E8F0';
  const inputBg = isDark ? '#0F172B' : '#FFFFFF';
  const inputBorder = isDark ? '#314158' : '#E2E8F0';
  const inputText = isDark ? '#E2E8F0' : '#314158';
  const cardBg = isDark ? '#1D293D' : '#F8FAFC';
  const cardBorder = isDark ? '#314158' : '#E2E8F0';
  const infoBg = isDark ? '#162456' : '#EFF6FF';
  const infoBorder = isDark ? '#1E3A8A' : '#BFDBFE';
  const infoText = isDark ? '#93C5FD' : '#1E40AF';
  const warningBg = isDark ? '#422006' : '#FFFBEB';
  const warningBorder = isDark ? '#92400E' : '#FDE68A';
  const warningText = isDark ? '#FCD34D' : '#92400E';
  const buttonGradient = 'linear-gradient(90deg, #2B7FFF 0%, #AD46FF 100%)';
  const disabledLabelColor = isDark ? '#62748E' : '#90A1B9';
  const iconButtonBg = isDark ? '#162456' : '#EFF6FF';
  const iconButtonColor = isDark ? '#51A2FF' : '#2B7FFF';
  const dangerBg = isDark ? '#3B0A0A' : '#FEF2F2';
  const dangerColor = isDark ? '#FF637E' : '#EF4444';
  const itemBg = isDark ? '#1D293D' : '#F8FAFC';
  const itemBorder = isDark ? '#314158' : '#E2E8F0';
  const itemText = isDark ? '#E2E8F0' : '#314158';
  const editBtnBg = isDark ? '#1D293D' : '#F8FAFC';
  const editBtnText = isDark ? '#CAD5E2' : '#45556C';

  const openModal = () => setModalOpen(true);
  const closeModal = () => setModalOpen(false);

  const saveSettings = async (data: any) => {
    try {
      let result: any;
      switch (settingsType) {
        case "toggle":
          result = await manageCommentsSettings(selectedUser, actionID, settingsType, "edit", data);
          fetchSettings();
          return "General";

        case "keyword_list":
          switch (settingsSubType) {
            case "add":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, [data.keywordName]);
              fetchSettings();
              return "Adding Keyword";
            case "edit":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { old: data.oldKeywordName, new: data.keywordName });
              fetchSettings();
              return "Editing Keyword";
            case "remove":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType,data);
              fetchSettings();
              return "Deleting Keyword";
            default:
              return "Unknown Keyword Action";
          }

        case "template_list":
          switch (settingsSubType) {
            case "add":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, [{ template_name: data.header, template_data: data.value }]);
              fetchSettings();
              return "Adding Template";
            case "edit":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType,{ template_name: data.oldHeader, template_data: data.value });
              fetchSettings();
              return "Editing Template";
            case "remove":
              if ((settingsCategories.find(category => category.title === "template_list")?.options?.length ?? 0) === 1) {
                toast.error(t('likesSettings.errors.cannotDeleteDefaultTemplate'));
                return "Deleting Template";
              }
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: data.oldHeader });
              fetchSettings();
              return "Deleting Template";
            default:
              return "Unknown Template Action";
          }

        case "text_list":
          switch (settingsSubType) {
            case "add":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, [{ template_name: data.header, template_data: data.value }]);
              fetchSettings();
              return "Adding Template";
            case "edit":
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: data.oldHeader, template_data: data.value });
              fetchSettings();
              return "Editing Template";
            case "remove":
              if ((settingsCategories.find(category => category.title === "text_list")?.options?.length ?? 0) === 1) {
                toast.error(t('likesSettings.errors.cannotDeleteDefaultTemplate'));
                return "Deleting Template";
              }
              result = await manageCommentsSettings(selectedUser, actionID, settingsType, settingsSubType, { template_name: data.oldHeader });
              fetchSettings();
              return "Deleting Template";
            default:
              return "Unknown Template Action";
          }

        default:
          return "Unknown Settings Type";
      }
    } catch (err: any) {
      console.error("Error saving settings:", err);
      setError(err.message);
    }
  };

  const fetchSettings = async () => {
    try {
      if (!selectedUser) throw new Error("No user selected");
      const response: any = await getLikesSettings(selectedUser);
      setSettings(response || []);
      setLoading(false);
    } catch (err: any) {
      console.error("Error fetching settings:", err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedUser) {
      fetchSettings();
    }
  }, [selectedUser]);

  const toggleSetting = async (actionId: string, category: string, option: Setting, newValue: boolean) => {
    try {
      setSettings(prev => prev.map(item =>
        item.action_id === actionId && item.settings_type === category
          ? { ...item, settings_value: newValue }
          : item
      ));

      await manageCommentsSettings(selectedUser, actionId, category, "edit", newValue);
    } catch (err: any) {
      console.error("Error toggling setting:", err);
      setError(err.message);
      setSettings(prev => prev.map(item =>
        item.action_id === actionId && item.settings_type === category
          ? { ...item, settings_value: !newValue }
          : item
      ));
    }
  };

  const handleAddKeyword = (optionIndex: number) => {
    setSettings(prev => prev.map((item, idx) =>
      idx === optionIndex
        ? { ...item, settings_value: [...item.settings_value, ""] }
        : item
    ));
  };

  const handleKeywordChange = (optionIndex: number, keyIndex: number, value: string) => {
    setSettings(prev => prev.map((item, idx) => {
      if (idx === optionIndex) {
        const newValues = [...item.settings_value];
        newValues[keyIndex] = value;
        return { ...item, settings_value: newValues };
      }
      return item;
    }));
  };

  const handleTemplateChange = (optionIndex: number, templateIndex: number, field: string, value: string) => {
    setSettings(prev => prev.map((item, idx) => {
      if (idx === optionIndex) {
        const newTemplates = item.settings_value.map((tpl: any, tplIdx: number) =>
          tplIdx === templateIndex ? { ...tpl, [field]: value } : tpl
        );
        return { ...item, settings_value: newTemplates };
      }
      return item;
    }));
  };

  if (loading) {
     return <Spinner size="large" text={t('likesSettings.loading')} fullPage />;
  }

  if (error) {
    return (
      <div style={{
        backgroundColor: cardBg,
        borderRadius: 12,
        border: `1px solid ${cardBorder}`,
        padding: 32,
        maxWidth: 400,
        margin: '32px auto',
        textAlign: 'center'
      }}>
        <div style={{
          width: 64,
          height: 64,
          backgroundColor: dangerBg,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 16px'
        }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={dangerColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" />
            <line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
        </div>
        <h3 style={{ fontSize: 16, fontWeight: 600, color: sectionHeaderColor, marginBottom: 8 }}>
          {t('likesSettings.errors.loadingError')}
        </h3>
        <p style={{ fontSize: 12, color: descriptionColor, marginBottom: 16 }}>{error}</p>
        <button
          style={{
            padding: '8px 16px',
            background: buttonGradient,
            color: '#FFFFFF',
            borderRadius: 8,
            border: 'none',
            fontSize: 12,
            fontWeight: 500,
            cursor: 'pointer'
          }}
          onClick={() => window.location.reload()}
        >
          {t('likesSettings.buttons.retry')}
        </button>
      </div>
    );
  }

  const settingsCategories: SettingsCategory[] = [...new Set(settings.map(item => item.settings_type))].map(settingsType => {
    const filteredSettings = settings.filter(item => item.settings_type === settingsType);
    return {
      title: settingsType,
      options: filteredSettings
    };
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {isModalOpen && (
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSave={saveSettings}
          selectedSettingsType={settingsType}
          selectedSettingsSubType={settingsSubType}
          selectedOldKeywordName={oldKeywordName}
          selectedOldHeader={oldHeader}
          selectedOldValue={oldValue}
        />
      )}

      {settingsCategories.map((category, index) => (
        <div key={index}>
          {/* Section header */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            paddingBottom: 16,
            borderBottom: `1px solid ${sectionBorder}`,
            marginBottom: 20
          }}>
            <h3 style={{
              fontSize: 16,
              fontWeight: 700,
              color: sectionHeaderColor,
              margin: 0
            }}>
              {{
                toggle: t('likesSettings.sections.generalSettings'),
                keyword_list: t('likesSettings.sections.keywords'),
                template_list: t('likesSettings.sections.templates'),
                text_list: t('likesSettings.sections.textLists')
              }[category.title] || category.title}
            </h3>
          </div>

          {/* Toggle settings */}
          {category.title === "toggle" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.some(opt => opt.category_enabled === false) && (
                <div style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 8,
                  padding: 12,
                  backgroundColor: warningBg,
                  border: `1px solid ${warningBorder}`,
                  borderRadius: 8
                }}>
                  <p style={{ fontSize: 12, color: warningText, margin: 0 }}>{t('likesSettings.warnings.categoryDisabled')}</p>
                </div>
              )}
              {category.options.map((option, optIndex) => {
                const isReadOnly = option.category_enabled === false;
                const isOn = option.settings_value || false;
                return (
                  <div key={optIndex} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
                      <span style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 500,
                        color: isReadOnly ? disabledLabelColor : labelColor
                      }}>
                        {option.description}
                      </span>
                      <span style={{
                        fontSize: 14,
                        lineHeight: '20px',
                        fontWeight: 400,
                        color: descriptionColor
                      }}>
                        {t('likesSettings.descriptions.toggleSetting', 'Toggle this setting on or off.')}
                      </span>
                    </div>
                    <div
                      onClick={() => {
                        if (isReadOnly) return;
                        const newValue = !isOn;
                        setActionID(option.action_id);
                        setSettingsType("toggle");
                        setSettingsSubType("edit");
                        toggleSetting(option.action_id, "toggle", option, newValue);
                      }}
                      style={{
                        width: 40,
                        height: 20,
                        borderRadius: 999,
                        backgroundColor: isOn ? switchOnBg : switchOffBg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: isOn ? 'flex-end' : 'flex-start',
                        padding: 2,
                        cursor: isReadOnly ? 'not-allowed' : 'pointer',
                        opacity: isReadOnly ? 0.5 : 1,
                        transition: 'background-color 0.2s ease',
                        flexShrink: 0
                      }}
                    >
                      <div style={{
                        width: 16,
                        height: 16,
                        borderRadius: 999,
                        backgroundColor: switchKnob,
                        transition: 'all 0.2s ease'
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Keyword list settings */}
          {category.title === "keyword_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12
                  }}>
                    <span style={{ fontSize: 12, color: labelColor, fontWeight: 500 }}>{option.description}</span>
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        backgroundColor: iconButtonBg,
                        color: iconButtonColor,
                        borderRadius: 8,
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 700
                      }}
                      onClick={() => {
                        setSettingsType('keyword_list');
                        setSettingsSubType('add');
                        setModalOpen(true);
                        setActionID(option.action_id);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {option.settings_value.map((keyword: string, keyIndex: number) => (
                      <div key={keyIndex} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: 12,
                        backgroundColor: itemBg,
                        borderRadius: 8,
                        border: `1px solid ${itemBorder}`
                      }}>
                        <div style={{ flex: 1 }}>
                          {editingKeyword.optionIndex === optIndex && editingKeyword.index === keyIndex ? (
                            <input
                              type="text"
                              value={keyword}
                              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleKeywordChange(
                                settings.findIndex(s => s._id === option._id),
                                keyIndex,
                                e.target.value
                              )}
                              onBlur={() => setEditingKeyword({ index: -1, optionIndex: -1 })}
                              style={{
                                width: '100%',
                                padding: '4px 8px',
                                fontSize: 12,
                                border: `1px solid ${inputBorder}`,
                                borderRadius: 6,
                                backgroundColor: inputBg,
                                color: inputText,
                                outline: 'none'
                              }}
                              autoFocus
                            />
                          ) : (
                            <span style={{ fontSize: 12, color: itemText }}>{keyword}</span>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <button
                            style={{
                              width: 32,
                              height: 32,
                              backgroundColor: iconButtonBg,
                              color: iconButtonColor,
                              borderRadius: 6,
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setSettingsType('keyword_list');
                              setSettingsSubType('edit');
                              setModalOpen(true);
                              setOldKeywordName(keyword);
                              setActionID(option.action_id);
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                          <button
                            style={{
                              width: 32,
                              height: 32,
                              backgroundColor: dangerBg,
                              color: dangerColor,
                              borderRadius: 6,
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                            onClick={() => {
                              setSettingsType('keyword_list');
                              setSettingsSubType('remove');
                              setModalOpen(true);
                              setOldKeywordName(keyword);
                              setActionID(option.action_id);
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6" />
                              <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Template list settings */}
          {category.title === "template_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12
                  }}>
                    <span style={{ fontSize: 12, color: labelColor, fontWeight: 500 }}>{option.description}</span>
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        backgroundColor: iconButtonBg,
                        color: iconButtonColor,
                        borderRadius: 8,
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 700
                      }}
                      onClick={() => {
                        setSettingsType('template_list');
                        setSettingsSubType('add');
                        setModalOpen(true);
                        setActionID(option.action_id);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {option.settings_value.map((template: { template_name: string; template_data: string }, tplIndex: number) => (
                      <div key={tplIndex} style={{
                        backgroundColor: cardBg,
                        borderRadius: 8,
                        border: `1px solid ${cardBorder}`,
                        overflow: 'hidden'
                      }}>
                        <div style={{ padding: 16 }}>
                          <p style={{
                            fontSize: 12,
                            color: itemText,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0
                          }}>
                            {template.template_data}
                          </p>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 16px',
                          borderTop: `1px solid ${cardBorder}`
                        }}>
                          <button
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              fontSize: 12,
                              color: editBtnText,
                              backgroundColor: editBtnBg,
                              borderRadius: 6,
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => {
                              setSettingsType('template_list');
                              setSettingsSubType('edit');
                              setModalOpen(true);
                              setOldHeader(template.template_name);
                              setOldValue(template.template_data);
                              setActionID(option.action_id);
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                          {(settingsCategories.find(category => category.title === "template_list")?.options?.length ?? 0) !== 1 && (
                            <button
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                fontSize: 12,
                                color: dangerColor,
                                backgroundColor: dangerBg,
                                borderRadius: 6,
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => {
                                setSettingsType('template_list');
                                setSettingsSubType('remove');
                                setModalOpen(true);
                                setOldHeader(template.template_name);
                                setOldValue(template.template_data);
                                setActionID(option.action_id);
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                              </svg>
                              <span>Delete</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Text list settings */}
          {category.title === "text_list" && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              {category.options.map((option, optIndex) => (
                <div key={optIndex}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12
                  }}>
                    <span style={{ fontSize: 12, color: labelColor, fontWeight: 500 }}>{option.description}</span>
                    <button
                      style={{
                        width: 32,
                        height: 32,
                        backgroundColor: iconButtonBg,
                        color: iconButtonColor,
                        borderRadius: 8,
                        border: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        fontSize: 16,
                        fontWeight: 700
                      }}
                      onClick={() => {
                        setSettingsType('text_list');
                        setSettingsSubType('add');
                        setModalOpen(true);
                        setActionID(option.action_id);
                      }}
                    >
                      +
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {option.settings_value.map((template: { template_name: string; template_data: string }, tplIndex: number) => (
                      <div key={tplIndex} style={{
                        backgroundColor: cardBg,
                        borderRadius: 8,
                        border: `1px solid ${cardBorder}`,
                        overflow: 'hidden'
                      }}>
                        <div style={{ padding: 16 }}>
                          <p style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: sectionHeaderColor,
                            marginBottom: 8,
                            marginTop: 0
                          }}>
                            {template.template_name}
                          </p>
                          <p style={{
                            fontSize: 12,
                            color: itemText,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0
                          }}>
                            {template.template_data}
                          </p>
                        </div>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '8px 16px',
                          borderTop: `1px solid ${cardBorder}`
                        }}>
                          <button
                            style={{
                              flex: 1,
                              padding: '8px 12px',
                              fontSize: 12,
                              color: editBtnText,
                              backgroundColor: editBtnBg,
                              borderRadius: 6,
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: 8,
                              cursor: 'pointer',
                              transition: 'all 0.2s ease'
                            }}
                            onClick={() => {
                              setSettingsType('text_list');
                              setSettingsSubType('edit');
                              setModalOpen(true);
                              setOldHeader(template.template_name);
                              setOldValue(template.template_data);
                              setActionID(option.action_id);
                            }}
                          >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                            <span>Edit</span>
                          </button>
                          {(settingsCategories.find(category => category.title === "text_list")?.options?.length ?? 0) !== 1 && (
                            <button
                              style={{
                                flex: 1,
                                padding: '8px 12px',
                                fontSize: 12,
                                color: dangerColor,
                                backgroundColor: dangerBg,
                                borderRadius: 6,
                                border: 'none',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 8,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease'
                              }}
                              onClick={() => {
                                setSettingsType('text_list');
                                setSettingsSubType('remove');
                                setModalOpen(true);
                                setOldHeader(template.template_name);
                                setOldValue(template.template_data);
                                setActionID(option.action_id);
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                              </svg>
                              <span>Delete</span>
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default LikesSettings;
